import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { InputField } from "@/components/ui/input-field";
import { useTranslation } from "react-i18next";
import { AuthUserPayload, AuthUserSchema } from "@/schemas/auth";
import { useAuthStore } from "@/stores/auth-store";
import { useSignIn } from "@/queries/use-sign-in";
import { useEffect } from "react";
import { AUTH_ROUTES, PROTECTED_ROUTES } from "@/utils/navigation";

export const Route = createFileRoute("/(auth)/sign-in/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const router = useRouter();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated()) {
      router.navigate({ to: PROTECTED_ROUTES.CALENDAR });
    }
  }, [isAuthenticated, router]);

  const form = useForm<AuthUserPayload>({
    resolver: zodResolver(AuthUserSchema()),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = form;

  const { mutate: signIn, isPending: isSigningIn } = useSignIn({
    onSuccess: () => {
      router.navigate({ to: PROTECTED_ROUTES.CALENDAR });
    },
    onError: (error: Error) => {
      const errorMessage = error.message;
      setError("email", {
        type: "manual",
      });
      setError("password", {
        type: "manual",
        message: errorMessage,
      });
    },
  });

  const onSignInSubmit = (data: AuthUserPayload) => {
    form.clearErrors();
    signIn(data);
  };

  return (
    <div className="flex w-full max-w-[262px] flex-col items-center md:max-w-[340px] md:items-start lg:max-w-[376px]">
      <h2 className="text-center text-xl font-bold text-gray-800 md:text-left md:text-2xl lg:text-[32px] dark:text-white">
        {t("pages:sign-in.title")}
      </h2>

      <form
        className="mt-8 flex w-full flex-col md:mt-6"
        onSubmit={handleSubmit(onSignInSubmit)}
        noValidate
      >
        <InputField.Text
          id="email"
          data-testid="sign-in-email"
          type="email"
          placeholder="email@email.com"
          label={t("generic:forms.fields.email")}
          error={errors.email}
          {...register("email")}
          className="dak:bg-gray-800 mb-4 md:mb-6 lg:mb-4"
        />

        <InputField.Password
          id="password"
          data-testid="sign-in-password"
          type="password"
          placeholder="**********"
          label={t("generic:forms.fields.password")}
          error={errors.password}
          {...register("password")}
          className="mb-2 md:mb-3"
        />

        <Link
          to={AUTH_ROUTES.FORGOT_PASSWORD}
          data-testid="sign-in-forgot-password"
          className="cursor-pointer text-sm text-gray-800 md:text-[14px] lg:text-[16px] dark:text-white"
        >
          {t("pages:sign-in.forgotPassword")}
        </Link>

        <Button
          type="submit"
          data-testid="sign-in-submit"
          className="mt-10 w-full lg:mt-14"
          loading={isSigningIn}
        >
          {t("pages:sign-in.submit")}
        </Button>
      </form>
    </div>
  );
}
